import axios from 'axios';
import { safeUrl, validateEmailHtml } from './contracts';

export const librarySources = {
  professional: '/email-templates',
  saved: '/user/template/list',
  emails: '/email/template/list'
};

export async function fetchLibraryPage(collection, page = 1, search = '') {
  const response = await axios.get(librarySources[collection], {
    params: { page, limit: 24, search }, timeout: 20000
  });
  const data = response.data;
  if (!data || !Number.isFinite(Number(data.last_page)))
    throw new Error('The template library returned an invalid response. Please try again.');
  return {
    items: (data.data || []).map(item => ({
      ...item,
      id: `${collection}:${item.id}`,
      sourceId: String(item.id),
      source: collection,
      subject: '',
      status: 'active'
    })),
    total: Number(data.total || 0),
    lastPage: Math.max(1, Number(data.last_page))
  };
}

// Use an unauthenticated request, never the app's token-bearing axios client.
export async function fetchProfessionalHtml(template) {
  const thumbnail = template.preview_image || template.thumbnail || '';
  if (!/^https:\/\/assets\.grooveapps\.com\/email-templates\/[a-zA-Z0-9_-]+_large\.jpg$/.test(thumbnail))
    throw new Error('The original HTML for this design is unavailable. Choose another template or try again.');
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(thumbnail.replace(/_large\.jpg$/, '.html'), {
      credentials: 'omit', signal: controller.signal, referrerPolicy: 'no-referrer'
    });
    if (!response.ok) throw new Error('Could not load this template. Please try again.');
    const html = repairAssetHtml(await response.text());
    if (html.length > 500000) throw new Error('This template is too large to edit.');
    return html;
  } finally { clearTimeout(timeout); }
}

export function repairAssetHtml(html) {
  // A historical asset upload prefixed *every character* of Indoor Plant Sale
  // with its asset directory. Reverse only that exact, verifiable corruption.
  const prefix = (html.match(/^https:\/\/assets\.grooveapps\.com\/email-templates\/[a-zA-Z0-9_-]+\//) || [])[0];
  if (prefix && html.startsWith(prefix + '<' + prefix + '!')) {
    const characters = html.split(prefix);
    if (characters.length > 100 && characters.every(character => Array.from(character).length <= 1)) return characters.join('');
  }
  return html;
}

function decodeProtectedEmail(value) {
  if (!/^(?:[0-9a-f]{2}){2,}$/i.test(value || '')) return '';
  const key = parseInt(value.slice(0, 2), 16);
  return value.slice(2).match(/../g).map(byte => String.fromCharCode(parseInt(byte, 16) ^ key)).join('');
}

export function prepareImportedEmail(html) {
  if (typeof html === 'string') html = repairAssetHtml(html);
  if (typeof html !== 'string' || !/<[a-z][\s\S]*>/i.test(html))
    throw new Error('This template has no saved HTML. Its original data has not been changed.');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Web preview metadata and remote font stylesheets are not email content.
  // Keep the design CSS, responsive rules, tables, image URLs and conditional comments.
  doc.querySelectorAll('link,meta[http-equiv],script').forEach(node => node.remove());
  doc.querySelectorAll('[data-cfemail]').forEach(node => {
    const email = decodeProtectedEmail(node.getAttribute('data-cfemail'));
    if (email) node.textContent = email;
  });
  doc.querySelectorAll('a[href^="/cdn-cgi/l/email-protection"]').forEach(node => {
    const encoded = (node.getAttribute('href').split('#')[1]) || (node.querySelector('[data-cfemail]') || node).getAttribute('data-cfemail');
    const email = decodeProtectedEmail(encoded);
    if (email) node.setAttribute('href', 'mailto:' + email);
    else { node.setAttribute('href', '#'); node.setAttribute('data-groove-needs-link', 'true'); }
  });
  doc.querySelectorAll('form').forEach(node => {
    const link = doc.createElement('a');
    link.textContent = 'Complete the form on our website';
    link.setAttribute('href', '#');
    link.setAttribute('data-groove-needs-link', 'true');
    node.replaceWith(link);
  });
  doc.querySelectorAll('iframe').forEach(node => {
    const link = doc.createElement('a');
    link.textContent = 'Watch the video';
    link.setAttribute('href', '#');
    link.setAttribute('data-groove-needs-link', 'true');
    node.replaceWith(link);
  });
  // Email clients cannot run checkbox-driven navigation. Show its ordinary links.
  doc.querySelectorAll('input.menu-checkbox,input.bee-hamburger-trigger').forEach(node => {
    doc.querySelectorAll('label').forEach(label => { if (label.htmlFor === node.id) label.remove(); });
    node.remove();
  });
  const emailCss = css => css
    .replace(/url\(\s*(["']?)data:image\/svg\+xml;base64,[a-zA-Z0-9+/=]+\1\s*\)/gi, 'none')
    .replace(/url\(https:\/\/d1oco4z2z1fhwp\.cloudfront\.net\/templates\/default\/\d+\/svg\+xml;base64,[a-zA-Z0-9+/=]+\)/gi, 'none')
    .replace(/url\(\s*(?:""|'')?\s*\)/gi, 'none');
  doc.querySelectorAll('style').forEach(node => { node.textContent = emailCss(node.textContent); });
  for (const node of doc.querySelectorAll('*')) {
    for (const attr of Array.from(node.attributes)) {
      if (/^on/i.test(attr.name)) node.removeAttribute(attr.name);
      if (attr.name === 'style') node.setAttribute('style', emailCss(attr.value));
      if (/^(href|src|background)$/i.test(attr.name) && attr.value && !safeUrl(attr.value.trim())) {
        if (attr.name === 'href' && /^(\*\|UNSUB\|\*|\[Unsubscribe\])$/i.test(attr.value)) node.setAttribute('href', '{unsubscribe_link}');
        else if (/^https?\/\//i.test(attr.value)) node.setAttribute(attr.name, attr.value.replace(/^(https?)/i, '$1:'));
        else if (/^\/\//.test(attr.value)) node.setAttribute(attr.name, 'https:' + attr.value);
        else if (/^(?:[\w-]+\.)+[a-z]{2,}(?:[/:?#]|$)/i.test(attr.value)) node.setAttribute(attr.name, 'https://' + attr.value);
        else if (attr.name === 'href') {
          node.setAttribute('href', '#');
          node.setAttribute('data-groove-needs-link', 'true');
        }
      }
    }
  }
  // Web-exported rows use flex, which desktop Outlook cannot render. Keep their
  // CSS classes and content, but use email presentation tables for the columns.
  doc.querySelectorAll('div.bee-row-content').forEach(row => {
    const table = doc.createElement('table');
    Array.from(row.attributes).forEach(attr => table.setAttribute(attr.name, attr.value));
    table.setAttribute('role', 'presentation'); table.setAttribute('width', '100%');
    table.setAttribute('cellpadding', '0'); table.setAttribute('cellspacing', '0');
    table.style.display = 'table'; table.style.borderCollapse = 'collapse';
    const tr = table.insertRow();
    Array.from(row.children).forEach(column => {
      const cell = tr.insertCell();
      Array.from(column.attributes).forEach(attr => cell.setAttribute(attr.name, attr.value));
      const width = (column.className.match(/bee-col-w(\d+)/) || [])[1];
      if (width) cell.setAttribute('width', (Number(width) / 12 * 100) + '%');
      cell.style.verticalAlign = 'top';
      while (column.firstChild) cell.appendChild(column.firstChild);
    });
    row.replaceWith(table);
  });
  const result = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  validateEmailHtml(result);
  return result;
}

export async function resolveLibraryTemplate(template) {
  if (!template.source || template.source === 'studio') return template;
  let html;
  if (template.source === 'professional') html = await fetchProfessionalHtml(template);
  else {
    const prefix = template.source === 'saved' ? '/user/template/' : '/email/template/';
    const response = await axios.get(prefix + encodeURIComponent(template.sourceId), { timeout: 20000 });
    html = response.data.message;
  }
  const bodyHtml = prepareImportedEmail(html);
  const doc = new DOMParser().parseFromString(bodyHtml, 'text/html');
  return { ...template, document: null, bodyHtml, bodyText: doc.body.textContent || '' };
}

export function editableTemplateId(template) {
  // Professional, imported user templates and past emails always create a copy.
  return template.source === 'studio' ? template.id : '';
}

export function legacyLibraryRedirect(route) {
  return {
    path: '/templates/new',
    query: { collection: route.query.type === 'email-templates' ? 'saved' : 'professional' }
  };
}
